import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  CloudSync as SyncIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../services/api';

const MotionCard = motion(Card);

const DropboxSync = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const [syncLimit, setSyncLimit] = useState(10);
  const [dropboxFiles, setDropboxFiles] = useState([]);
  const [lastSyncStats, setLastSyncStats] = useState(null);

  // Configuration form state
  const [config, setConfig] = useState({
    app_key: '',
    app_secret: '',
    refresh_token: '',
    sync_path: '/iStock',
    sync_interval_minutes: 30,
    max_files_per_sync: 10,
  });

  useEffect(() => {
    loadSyncStatus();
  }, []);

  const loadSyncStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sync/dropbox/status');
      setSyncStatus(response.data);

      if (response.data.config_exists) {
        setConfig(prev => ({
          ...prev,
          sync_path: response.data.sync_path || '/iStock',
          sync_interval_minutes: response.data.sync_interval_minutes || 30,
          max_files_per_sync: response.data.max_files_per_sync || 10,
        }));
        setSyncLimit(response.data.max_files_per_sync || 10);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading sync status:', err);
      setError('Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };

  const loadDropboxFiles = async () => {
    try {
      const response = await api.get('/sync/dropbox/files?limit=20');
      setDropboxFiles(response.data.files);
    } catch (err) {
      console.error('Error loading Dropbox files:', err);
      setError('Failed to load Dropbox files');
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post(`/sync/dropbox?limit=${syncLimit}`, {}, {
        timeout: 300000 // 5 minutes timeout for sync operations
      });
      setLastSyncStats(response.data.stats);
      setSuccess(response.data.message);

      // Refresh status and files
      await loadSyncStatus();
      if (dropboxFiles.length > 0) {
        await loadDropboxFiles();
      }
    } catch (err) {
      console.error('Sync error:', err);
      // Check if it's a timeout error
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError('Sync is taking longer than expected. Please check the results and try again if needed.');
      } else {
        setError(err.response?.data?.detail || 'Sync failed');
      }
    } finally {
      setSyncing(false);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await api.post('/sync/dropbox/configure', config);
      setSuccess('Configuration saved successfully');
      setConfigOpen(false);
      await loadSyncStatus();
    } catch (err) {
      console.error('Config save error:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          mb: 2,
          textAlign: 'center',
          background: 'linear-gradient(45deg, #667eea, #764ba2)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 600,
        }}
      >
        Dropbox Sync
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 4,
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.7)',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        Automatically sync images from your Dropbox account to the AI Photo Gallery.
        Configure your Dropbox API credentials and set up scheduled syncing.
      </Typography>

      {loading && (
        <LinearProgress
          sx={{
            mb: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
            }
          }}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sync Controls */}
        <Grid item xs={12} md={6}>
          <MotionCard
            whileHover={{ scale: 1.02 }}
            sx={{
              background: 'rgba(26, 26, 46, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <SyncIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Sync Control
                </Typography>
              </Box>

              {syncStatus && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    Configuration: {syncStatus.config_exists ? (
                      <Chip label="Configured" color="success" size="small" />
                    ) : (
                      <Chip label="Not Configured" color="error" size="small" />
                    )}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    Processed Files: {syncStatus.processed_files_count}
                  </Typography>

                  {syncStatus.sync_path && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      Sync Path: {syncStatus.sync_path}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Files to Sync"
                  type="number"
                  value={syncLimit}
                  onChange={(e) => setSyncLimit(parseInt(e.target.value) || 10)}
                  size="small"
                  sx={{
                    mr: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                  }}
                />

                <Button
                  variant="contained"
                  startIcon={syncing ? <LinearProgress size={20} /> : <SyncIcon />}
                  onClick={triggerSync}
                  disabled={syncing || !syncStatus?.config_exists}
                  sx={{
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    mr: 1,
                  }}
                >
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoSync}
                      onChange={(e) => setAutoSync(e.target.checked)}
                      disabled={!syncStatus?.config_exists}
                    />
                  }
                  label="Auto Sync"
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                />

                <Button
                  startIcon={<SettingsIcon />}
                  onClick={() => setConfigOpen(true)}
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  Configure
                </Button>
              </Box>

              {lastSyncStats && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(102, 126, 234, 0.1)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                    Last Sync Results
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip label={`Downloaded: ${lastSyncStats.downloaded}`} size="small" />
                    <Chip label={`Uploaded: ${lastSyncStats.uploaded}`} size="small" />
                    {lastSyncStats.errors > 0 && (
                      <Chip label={`Errors: ${lastSyncStats.errors}`} color="error" size="small" />
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Dropbox Files */}
        <Grid item xs={12} md={6}>
          <MotionCard
            whileHover={{ scale: 1.02 }}
            sx={{
              background: 'rgba(26, 26, 46, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FolderIcon sx={{ color: '#764ba2' }} />
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Available Files
                </Typography>
                <IconButton
                  onClick={loadDropboxFiles}
                  disabled={!syncStatus?.config_exists}
                  sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>

              {dropboxFiles.length > 0 ? (
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {dropboxFiles.map((file, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        {file.processed ? (
                          <SuccessIcon sx={{ color: '#4caf50' }} />
                        ) : (
                          <ImageIcon sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}>
                              {file.name}
                            </Typography>
                            {file.processed && (
                              <Chip
                                label="Synced"
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                  color: '#4caf50',
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                            {formatFileSize(file.size)} • {new Date(file.modified).toLocaleDateString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    {syncStatus?.config_exists
                      ? 'Click refresh to load files from Dropbox'
                      : 'Configure Dropbox to see available files'
                    }
                  </Typography>
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      {/* Configuration Dialog */}
      <Dialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon />
          Dropbox Configuration
          <IconButton
            onClick={() => setConfigOpen(false)}
            sx={{ ml: 'auto', color: 'rgba(255, 255, 255, 0.6)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="App Key"
                value={config.app_key}
                onChange={(e) => setConfig(prev => ({ ...prev, app_key: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="App Secret"
                type="password"
                value={config.app_secret}
                onChange={(e) => setConfig(prev => ({ ...prev, app_secret: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Refresh Token"
                value={config.refresh_token}
                onChange={(e) => setConfig(prev => ({ ...prev, refresh_token: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sync Path"
                value={config.sync_path}
                onChange={(e) => setConfig(prev => ({ ...prev, sync_path: e.target.value }))}
                placeholder="/iStock"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sync Interval (minutes)"
                type="number"
                value={config.sync_interval_minutes}
                onChange={(e) => setConfig(prev => ({ ...prev, sync_interval_minutes: parseInt(e.target.value) || 30 }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Max Files Per Sync"
                type="number"
                value={config.max_files_per_sync}
                onChange={(e) => setConfig(prev => ({ ...prev, max_files_per_sync: parseInt(e.target.value) || 10 }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            To get Dropbox API credentials, create an app at{' '}
            <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer">
              dropbox.com/developers/apps
            </a>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfigOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Cancel
          </Button>
          <Button
            onClick={saveConfig}
            variant="contained"
            sx={{ background: 'linear-gradient(45deg, #667eea, #764ba2)' }}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DropboxSync;